import jsPDF from 'jspdf';
import aluLogoUrl from '../../src/assets/ALU Logo.png';
import { API_BASE_URL } from '../../src/config';
import { resolveBackendAssetUrl } from '../../src/utils/assetUrl';
import { BASE_URL } from '../../src/api/client';

const DEFAULT_TEMPLATE_PATH = '/assets/membership-form-template.png';
const CANONICAL_TEMPLATE_SIZE = { width: 1414, height: 2000 };
const TEMPLATE_COORD_BASE = { width: 816, height: 1184 };
const PDF_IMAGE_SCALE = 2;
const TEMPLATE_IMAGE_QUALITY = 0.82;
const PHOTO_IMAGE_QUALITY = 0.8;

const UNION_CONFIG = {
  LWU: {
    short: 'LWU',
    full: 'Luzon Workers Union',
    logo: aluLogoUrl,
  },
  PPEU: {
    short: 'PPEU',
    full: 'Philippine Professional Employees Union',
    logo: aluLogoUrl,
  },
};
// Backward-compatible aliases for existing records
UNION_CONFIG.ALU = UNION_CONFIG.LWU;
UNION_CONFIG.APSUTEU = UNION_CONFIG.PPEU;

// Cache for branding data fetched from the backend
let _brandingCache = null;

const DEFAULT_UNION_KEY = 'LWU';

const withVersionQuery = (url, version) => {
  const normalizedUrl = normalize(url);
  if (!normalizedUrl) return null;
  const normalizedVersion = normalize(version);
  if (!normalizedVersion) return normalizedUrl;
  return `${normalizedUrl}${normalizedUrl.includes('?') ? '&' : '?'}v=${encodeURIComponent(normalizedVersion)}`;
};

const normalizeUnionKey = (affiliation) => {
  const normalized = normalize(affiliation).toUpperCase();
  return normalized || DEFAULT_UNION_KEY;
};

const fetchBrandingData = async () => {
  if (_brandingCache) return _brandingCache;
  try {
    const resp = await fetch(`${BASE_URL}/api/union-data/branding`);
    if (resp.ok) {
      const data = await resp.json();
      if (data?.affiliations) {
        _brandingCache = {};
        for (const aff of data.affiliations) {
          if (!aff?.acronym) continue;
          const key = aff.acronym.trim().toUpperCase();
          const rawVersion = aff.updatedAt || aff.updated_at || '';
          const logoVersion = rawVersion ? Date.parse(rawVersion) || String(rawVersion).trim() : '';
          _brandingCache[key] = {
            short: key,
            full: aff.fullName || key,
            logo: aff.logoPath
              ? withVersionQuery(resolveBackendAssetUrl(aff.logoPath), logoVersion)
              : null,
          };
        }
        return _brandingCache;
      }
    }
  } catch (_err) { /* fall back to static config */ }
  return {};
};

const getUnionConfig = async (affiliation) => {
  const key = normalizeUnionKey(affiliation);
  const staticConfig = UNION_CONFIG[key] || UNION_CONFIG[DEFAULT_UNION_KEY];
  const branding = await fetchBrandingData();
  if (branding[key]) {
    return {
      short: branding[key].short || staticConfig?.short || key,
      full: branding[key].full || staticConfig?.full || key,
      logo: branding[key].logo || staticConfig?.logo || aluLogoUrl,
    };
  }
  return staticConfig;
};

const buildTemplateSvg = (union) => {
  const isAPSUTEU = union.short === 'PPEU';
  const privacyFontSize = isAPSUTEU ? 8 : 9;
  return `
<svg width="1414" height="2000" viewBox="0 0 816 1184" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
  <defs>
    <style>
      .label { font-family: Arial, sans-serif; font-size: 14px; font-weight: bold; fill: #000; }
      .header-text { font-family: Arial, sans-serif; font-weight: bold; fill: #00205B; }
      .sub-text { font-family: Arial, sans-serif; font-size: 10px; fill: #000; }
      .section-header { fill: #00aeef; }
      .section-title { font-family: Arial, sans-serif; font-size: 18px; font-weight: bold; fill: #fff; letter-spacing: 2px; text-transform: uppercase; }
      .input-box { fill: none; stroke: #000; stroke-width: 0.7; }
      .checkbox { fill: none; stroke: #000; stroke-width: 0.7; }
      .tiny-text { font-family: Arial, sans-serif; font-size: ${privacyFontSize}px; fill: #000; }
      .title-main { font-size: 24px; font-weight: 800; fill: #00205B; }
      .title-sub { font-size: 14px; font-weight: bold; fill: #000; }
      .alu-text { font-family: Arial, sans-serif; font-weight: 900; fill: #CE1126; stroke: #fff; stroke-width: 0.5px; }
      .ring-text { font-family: Arial, sans-serif; font-weight: bold; font-size: 5px; fill: #CE1126; }
      .tri-text { font-family: Arial, sans-serif; font-weight: bold; font-size: 5.5px; fill: #fff; }
      .ribbon-text { font-family: Arial, sans-serif; font-weight: bold; font-size: 5px; fill: #CE1126; }
    </style>
    <symbol id="gear-tooth">
       <path d="M-1.5,-48 L-1.5,-52 L1.5,-52 L1.5,-48" fill="none" stroke="#00205B" stroke-width="2" />
       <circle cx="0" cy="-52" r="1.5" fill="#00205B" />
    </symbol>
  </defs>

  <rect width="100%" height="100%" fill="white"/>

  ${isAPSUTEU
    ? `<text x="130" y="38" class="header-text" font-size="14">Philippine Professional Employees Union</text>
  <text x="130" y="53" class="header-text" font-size="14"></text>
  <text x="130" y="68" class="sub-text" font-size="11">Luzon Regional Office</text>
  <line x1="130" y1="76" x2="420" y2="74" stroke="#00205B" stroke-width="1.5" />
  <text x="130" y="88" class="sub-text">262 ALU-APSU-ITF Bldg. 15th Ave. Brgy. Silangan,</text>
  <text x="130" y="100" class="sub-text">Cubao, Quezon City</text>`
    : `<text x="130" y="45" class="header-text" font-size="20">${union.full}</text>
  <text x="130" y="62" class="sub-text" font-size="11">Luzon Regional Office</text>
  <line x1="130" y1="70" x2="370" y2="68" stroke="#00205B" stroke-width="1.5" />
  <text x="130" y="82" class="sub-text">262 ALU-APSU-ITF Bldg. 15th Ave. Brgy. Silangan,</text>
  <text x="130" y="94" class="sub-text">Cubao, Quezon City</text>`}

  <text x="50" y="130" class="title-main">MEMBERSHIP</text>
  <text x="65" y="145" class="title-sub">REGISTRATION FORM</text>
  <rect x="50" y="135" width="10" height="10" fill="#00aeef" />

  <g transform="translate(629, 30)">
    <rect width="130" height="130" fill="none" stroke="#00aeef" stroke-width="3" />
    <text x="65" y="60" text-anchor="middle" font-family="Arial" font-size="12">2x2 picture</text>
    <text x="65" y="75" text-anchor="middle" font-family="Arial" font-size="12">here</text>
  </g>

  <rect x="50" y="161" width="710.5" height="35" class="section-header" />
  <rect x="50" y="160" width="15" height="35" fill="#255F9E" />
  <text x="80" y="185" class="section-title">PERSONAL INFORMATION</text>

  <text x="50" y="220" class="label">Full Name</text>
  <text x="160" y="220" class="label">:</text>
  <rect x="180" y="205" width="580" height="20" class="input-box" />

  <text x="50" y="255" class="label">Place of Birth</text>
  <text x="160" y="255" class="label">:</text>
  <rect x="180" y="240" width="240" height="20" class="input-box" />

  <text x="435" y="255" class="label">Date of Birth</text>
  <text x="550" y="255" class="label">:</text>
  <rect x="570" y="240" width="25" height="20" class="input-box" />
  <rect x="595" y="240" width="25" height="20" class="input-box" />
  <line x1="625" y1="250" x2="635" y2="250" stroke="black" stroke-width="2" />
  <rect x="640" y="240" width="25" height="20" class="input-box" />
  <rect x="665" y="240" width="25" height="20" class="input-box" />
  <line x1="695" y1="250" x2="705" y2="250" stroke="black" stroke-width="2" />
  <rect x="710" y="240" width="25" height="20" class="input-box" />
  <rect x="735" y="240" width="25" height="20" class="input-box" />

  <text x="50" y="290" class="label">Address</text>
  <text x="160" y="290" class="label">:</text>
  <rect x="180" y="275" width="580" height="20" class="input-box" />

  <text x="50" y="325" class="label">Status</text>
  <text x="160" y="325" class="label">:</text>
  <rect x="180" y="310" width="20" height="20" class="checkbox" />
  <text x="210" y="325" class="label">Single</text>
  <rect x="270" y="310" width="20" height="20" class="checkbox" />
  <text x="300" y="325" class="label">Married</text>
  <rect x="375" y="310" width="20" height="20" class="checkbox" />
  <text x="405" y="325" class="label">Divorce</text>
  <rect x="475" y="310" width="20" height="20" class="checkbox" />
  <text x="505" y="325" class="label">Others:</text>
  <line x1="560" y1="328" x2="650" y2="328" stroke="black" stroke-width="1" />
  <text x="670" y="325" class="label">Age  :</text>
  <rect x="710" y="310" width="50" height="20" class="input-box" />

  <text x="50" y="350" class="label">Educational</text>
  <text x="50" y="365" class="label">Attainment</text>
  <text x="160" y="360" class="label">:</text>
  <rect x="180" y="345" width="240" height="20" class="input-box" />

  <text x="435" y="360" class="label">No. of Children  :</text>
  <rect x="570" y="345" width="190" height="20" class="input-box" />

  <text x="50" y="395" class="label">Religion</text>
  <text x="160" y="395" class="label">:</text>
  <rect x="180" y="380" width="240" height="20" class="input-box" />

  <text x="435" y="395" class="label">Contact No.</text>
  <text x="550" y="395" class="label">:</text>
  <rect x="570" y="380" width="190" height="20" class="input-box" />

  <text x="50" y="430" class="label">E-Mail</text>
  <text x="160" y="430" class="label">:</text>
  <rect x="180" y="415" width="580" height="20" class="input-box" />

  <text x="50" y="465" class="label">Gender</text>
  <text x="160" y="465" class="label">:</text>
  <rect x="182" y="450" width="20" height="20" class="checkbox" />
  <text x="210" y="465" class="label">Man</text>
  <rect x="265" y="450" width="20" height="20" class="checkbox" />
  <text x="292" y="465" class="label">Woman</text>
  <rect x="372" y="450" width="20" height="20" class="checkbox" />
  <text x="402" y="450" class="label" font-size="12">Other (please state)</text>
  <line x1="402" y1="470" x2="528" y2="470" stroke="black" stroke-width="1" />
  <rect x="550" y="450" width="20" height="20" class="checkbox" />
  <text x="577" y="465" class="label">Prefer not to say</text>

  <rect x="50" y="490" width="710" height="35" class="section-header" />
  <rect x="50" y="490" width="15" height="35" fill="#255F9E" />
  <text x="80" y="515" class="section-title">EMPLOYMENT INFORMATION</text>

  <text x="50" y="550" class="label">Company</text>
  <text x="160" y="550" class="label">:</text>
  <rect x="178" y="535" width="580" height="20" class="input-box" />

  <text x="50" y="585" class="label">Position</text>
  <text x="160" y="585" class="label">:</text>
  <rect x="178" y="570" width="580" height="20" class="input-box" />

  <text x="50" y="620" class="label">Union Name</text>
  <text x="160" y="620" class="label">:</text>
  <rect x="178" y="605" width="240" height="20" class="input-box" />

  <text x="450" y="620" class="label">Years Employed :</text>
  <rect x="585" y="605" width="172" height="20" class="input-box" />

  <text x="50" y="655" class="label">Union Affiliation</text>
  <text x="160" y="655" class="label">:</text>
  <rect x="178" y="640" width="580" height="20" class="input-box" />

  <text x="50" y="690" class="label">Position in Union</text>
  <rect x="178" y="675" width="170" height="20" class="input-box" />
  <text x="385" y="690" class="label">Date of Membership  :</text>
  <rect x="560" y="675" width="198" height="20" class="input-box" />

  <rect x="50" y="720" width="710" height="35" class="section-header" />
  <rect x="50" y="720" width="15" height="35" fill="#255F9E" />
  <text x="80" y="745" class="section-title">CONTACT PERSON IN CASE OF EMERGENCY:</text>

  <text x="50" y="780" class="label">Full Name</text>
  <text x="160" y="780" class="label">:</text>
  <rect x="178" y="765" width="580" height="20" class="input-box" />

  <text x="50" y="815" class="label">Address</text>
  <text x="160" y="815" class="label">:</text>
  <rect x="178" y="800" width="580" height="20" class="input-box" />

  <text x="50" y="850" class="label">Contact No.</text>
  <text x="160" y="850" class="label">:</text>
  <rect x="178" y="835" width="188" height="20" class="input-box" />

  <text x="430" y="850" class="label">Relationship</text>
  <text x="545" y="850" class="label">:</text>
  <rect x="568" y="835" width="188" height="20" class="input-box" />

  <g transform="translate(30, 890)">
    <text x="0" y="10" class="tiny-text" font-weight="bold">A note on Data Privacy and Confidentiality:</text>
    <text x="0" y="25" class="tiny-text">The ${union.full} abides by the rules and regulations set by Republic Act No. 10173 or the</text>
    <text x="0" y="37" class="tiny-text">Data Privacy Act of 2012. All information provided in this form will be treated with utmost confidentiality.</text>
    <text x="0" y="65" class="tiny-text" font-weight="bold">Informed Consent:</text>
    <text x="0" y="80" class="tiny-text">The ${union.full} may compile statistics on personal and sensitive personal information I</text>
    <text x="0" y="92" class="tiny-text">have willfully submitted and declared in connection with my union membership application subject to</text>
    <text x="0" y="104" class="tiny-text">the provisions of the Philippine Data Privacy Act. I understand that all information in my application and</text>
    <text x="0" y="116" class="tiny-text">request are necessary and will be treated with utmost confidentiality.</text>
    <line x1="540" y1="60" x2="725" y2="60" stroke="#00aeef" stroke-width="1" />
    <text x="600" y="80" class="label" font-weight="normal">Signature</text>
  </g>

  <rect x="0" y="1150" width="816" height="10" fill="#0054a6" />
  <rect x="0" y="1165" width="816" height="19" fill="#00aeef" />
</svg>
`;
};

const buildTemplateDataUrl = (union) =>
  `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(buildTemplateSvg(union))}`;

const normalize = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') {
    return value.replace(/\s+/g, ' ').trim();
  }
  return String(value).replace(/\s+/g, ' ').trim();
};

const splitDateParts = (value) => {
  const normalized = normalize(value);
  if (!normalized) return { mm: '', dd: '', yy: '' };
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) {
    // Fallback: attempt to split raw string by common separators
    const parts = normalized.split(/[-/.\s]+/).filter(Boolean);
    return {
      mm: parts[0]?.padStart(2, '0') ?? '',
      dd: parts[1]?.padStart(2, '0') ?? '',
      yy: (parts[2]?.slice(-2) ?? ''),
    };
  }
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const yy = String(date.getFullYear()).slice(-2);
  return { mm, dd, yy };
};

const formatDateText = (value) => {
  const normalized = normalize(value);
  if (!normalized) return '';
  const date = new Date(normalized);
  if (!Number.isNaN(date.getTime())) {
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  }
  return normalized;
};

const calculateAge = (value) => {
  const normalized = normalize(value);
  if (!normalized) return '';
  const dob = new Date(normalized);
  if (Number.isNaN(dob.getTime())) return '';
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const beforeBirthday = today.getMonth() < dob.getMonth()
    || (today.getMonth() === dob.getMonth() && today.getDate() < dob.getDate());
  if (beforeBirthday) {
    age -= 1;
  }
  return age > 0 ? String(age) : '';
};

const relative = (x, y, width = 0) => ({
  x: x / TEMPLATE_COORD_BASE.width,
  y: y / TEMPLATE_COORD_BASE.height,
  width: width ? width / TEMPLATE_COORD_BASE.width : undefined,
});

const relativePoint = (x, y) => ({
  x: x / TEMPLATE_COORD_BASE.width,
  y: y / TEMPLATE_COORD_BASE.height,
});

const FIELD_COORDS = {
  fullName: relative(190, 212, 560), 
  placeOfBirth: relative(190, 247, 220),
  dateOfBirthMonth1: relative(570, 247, 25),
  dateOfBirthMonth2: relative(595, 247, 25),
  dateOfBirthDay1: relative(640, 247, 25),
  dateOfBirthDay2: relative(665, 247, 25),
  dateOfBirthYear1: relative(710, 247, 25),
  dateOfBirthYear2: relative(735, 247, 25),
  address: relative(190, 282, 560),
  statusOther: relative(560, 316, 90),
  age: relative(710, 316, 50),
  education: relative(190, 352, 220),
  children: relative(580, 352, 170),
  religion: relative(190, 387, 220),
  contactNumber: relative(580, 387, 170),
  email: relative(190, 422, 560),
  genderOther: relative(410, 458, 120),
  employer: relative(188, 542, 560),
  position: relative(188, 577, 560),
  unitSection: relative(188, 612, 225),
  yearsEmployed: relative(595, 612, 150),
  unionName: relative(188, 647, 560),
  unionPosition: relative(188, 682, 150),
  membershipDate: relative(570, 682, 180),
  emergencyName: relative(188, 772, 560),
  emergencyAddress: relative(188, 807, 560),
  emergencyContact: relative(188, 842, 168),
  emergencyRelationship: relative(578, 842, 168),
};

const PHOTO_FRAME = { x: 628.2, y: 30, width: 130, height: 130 };
const LOGO_AREA = { x: 52, y: 25, width: 72, height: 72 };
const SIGNATURE_AREA = { x: 520, y: 885, width: 250, height: 100 };

const STATUS_CHECKBOXES = {
  single: relativePoint(186, 314),
  married: relativePoint(276, 314),
  divorce: relativePoint(379, 314),
  others: relativePoint(481, 314),
};

const GENDER_CHECKBOXES = {
  man: relativePoint(190, 454),
  woman: relativePoint(271, 454),
  other: relativePoint(378, 454),
  prefer: relativePoint(556, 454),
};

const parseEmergencyContact = (raw) => {
  if (!raw) return {};
  if (typeof raw === 'object') {
    return {
      name: raw.fullName ?? raw.name ?? '',
      address: raw.address ?? '',
      phone: raw.phone ?? raw.contactNo ?? '',
      relationship: raw.relationship ?? '',
    };
  }

  const [nameAndRelation, phonePart] = String(raw).split(/[•|-]/).map((segment) => segment?.trim() ?? '');
  const relationMatch = nameAndRelation?.match(/\((.*?)\)/);
  return {
    name: relationMatch ? nameAndRelation.replace(relationMatch[0], '').trim() : nameAndRelation,
    phone: phonePart ?? '',
    relationship: relationMatch ? relationMatch[1] : '',
    address: '',
  };
};

const getDefaultTemplateUrl = () => {
  if (process.env.REACT_APP_MEMBERSHIP_TEMPLATE_URL) {
    return process.env.REACT_APP_MEMBERSHIP_TEMPLATE_URL;
  }
  const publicUrl = (process.env.PUBLIC_URL ?? '').replace(/\/$/, '');
  if (publicUrl) {
    return `${publicUrl}${DEFAULT_TEMPLATE_PATH}`;
  }
  if (typeof window !== 'undefined' && window.location) {
    return `${window.location.origin}${DEFAULT_TEMPLATE_PATH}`;
  }
  return DEFAULT_TEMPLATE_PATH;
};

const getFileBaseUrl = () => {
  return API_BASE_URL;
};

const buildPhotoUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('data:')) return url;
  return resolveBackendAssetUrl(url, {
    apiBaseUrl: getFileBaseUrl(),
    assumeProfilesDir: true,
  }) || '';
};

const loadTemplateImage = (url) => new Promise((resolve, reject) => {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => resolve(img);
  img.onerror = () => reject(new Error(`Unable to load membership form template from ${url}`));
  img.src = url;
});

const tryLoadTemplateImage = async (url) => {
  if (!url) return null;
  try {
    return await loadTemplateImage(url);
  } catch (err) {
    console.warn(err.message);
    return null;
  }
};

const fetchImageDataUrl = async (url) => {
  if (!url || typeof fetch === 'undefined' || typeof FileReader === 'undefined') return null;
  try {
    const res = await fetch(url, { mode: 'cors' });
    if (!res.ok) return null;
    const contentType = res.headers.get('content-type') || '';
    if (!contentType.startsWith('image/')) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch (err) {
    console.warn('Failed to fetch image as data URL:', err.message);
    return null;
  }
};

const imageToDataUrl = (image, options = {}) => {
  if (!image || typeof document === 'undefined') return null;
  const {
    format = 'image/jpeg',
    quality = 0.82,
    scale = PDF_IMAGE_SCALE,
  } = options;
  const canvas = document.createElement('canvas');
  // 2x keeps the form clear for print while significantly reducing PDF weight.
  const width = image.naturalWidth || CANONICAL_TEMPLATE_SIZE.width;
  const height = image.naturalHeight || CANONICAL_TEMPLATE_SIZE.height;

  canvas.width = width * scale;
  canvas.height = height * scale;
  
  const ctx = canvas.getContext('2d');
  // White background avoids dark/transparent artifacts in JPEG output.
  if (format === 'image/jpeg') {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL(format, quality);
};

const markCheckbox = (doc, coords, origin, dimensions) => {
  if (!coords) return;
  const size = dimensions.width * 0.015;
  doc.setFontSize(12);
  const offsetX = dimensions.width * (5 / TEMPLATE_COORD_BASE.width); // nudge checkmark a bit more to the right
  const yOffset = 0.5; // + down | - up
  doc.text('/', origin.x + coords.x * dimensions.width + offsetX, origin.y + coords.y * dimensions.height - yOffset, {
    baseline: 'top',
  });
  doc.setFontSize(9);
  return size;
};

const drawField = (doc, text, coords, origin, dimensions) => {
  if (!text || !coords) return;
  const value = normalize(text);
  if (!value) return;
  doc.setFontSize(9);
  const maxWidth = coords.width ? coords.width * dimensions.width : undefined;
  const lines = maxWidth ? doc.splitTextToSize(value, maxWidth) : value;
  const yOffset = 0.5; // mm-like visual nudge
  doc.text(lines, origin.x + coords.x * dimensions.width, origin.y + coords.y * dimensions.height - yOffset, {
    baseline: 'top',
  });
};

const paddedCoords = (coords, padPx = 3) => {
  if (!coords) return coords;
  const padRatio = padPx / TEMPLATE_COORD_BASE.width;
  return {
    ...coords,
    x: coords.x + padRatio,
    width: coords.width ? coords.width - padRatio * 2 : coords.width,
  };
};

const buildMembershipFormPdf = async (member = {}, options = {}) => {
  const union = await getUnionConfig(member.unionAffiliation);
  const templateUrl = options.templateUrl ?? getDefaultTemplateUrl();
  let templateImage = await tryLoadTemplateImage(templateUrl);
  if (!templateImage) {
    templateImage = await tryLoadTemplateImage(buildTemplateDataUrl(union));
  }

  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
    compress: true,
    putOnlyUsedFonts: true,
  });
  doc.setTextColor(15, 23, 42);

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const templateWidth = templateImage?.naturalWidth ?? CANONICAL_TEMPLATE_SIZE.width;
  const templateHeight = templateImage?.naturalHeight ?? CANONICAL_TEMPLATE_SIZE.height;
  const scale = Math.min(pageWidth / templateWidth, pageHeight / templateHeight);
  const drawnWidth = templateWidth * scale;
  const drawnHeight = templateHeight * scale;
  const offsetX = (pageWidth - drawnWidth) / 2;
  const offsetY = (pageHeight - drawnHeight) / 2;

  if (templateImage) {
    const templateData = imageToDataUrl(templateImage, {
      format: 'image/jpeg',
      quality: TEMPLATE_IMAGE_QUALITY,
      scale: PDF_IMAGE_SCALE,
    });
    if (templateData) {
      doc.addImage(templateData, 'JPEG', offsetX, offsetY, drawnWidth, drawnHeight, undefined, 'FAST');
    }
  }

  // Overlay union logo
  const logoImage = await tryLoadTemplateImage(union.logo);
  let logoData = logoImage ? imageToDataUrl(logoImage, { format: 'image/png', scale: 1.3 }) : null;
  if (!logoData && union.logo) {
    logoData = await fetchImageDataUrl(union.logo);
  }
  if (logoData) {
    const logoTargetW = drawnWidth * (LOGO_AREA.width / TEMPLATE_COORD_BASE.width);
    const logoTargetH = drawnHeight * (LOGO_AREA.height / TEMPLATE_COORD_BASE.height);
    const logoTargetX = offsetX + (LOGO_AREA.x / TEMPLATE_COORD_BASE.width) * drawnWidth;
    const logoTargetY = offsetY + (LOGO_AREA.y / TEMPLATE_COORD_BASE.height) * drawnHeight;
    const logoW = logoImage?.naturalWidth || LOGO_AREA.width;
    const logoH = logoImage?.naturalHeight || LOGO_AREA.height;
    const logoScale = Math.min(logoTargetW / logoW, logoTargetH / logoH);
    const logoRenderW = logoW * logoScale;
    const logoRenderH = logoH * logoScale;
    const logoRenderX = logoTargetX + (logoTargetW - logoRenderW) / 2;
    const logoRenderY = logoTargetY + (logoTargetH - logoRenderH) / 2;
    const logoFormat = logoData.startsWith('data:image/jpeg') || logoData.startsWith('data:image/jpg') ? 'JPEG' : 'PNG';
    doc.addImage(logoData, logoFormat, logoRenderX, logoRenderY, logoRenderW, logoRenderH, undefined, 'FAST');
  }

  const origin = { x: offsetX, y: offsetY };
  const dimensions = { width: drawnWidth, height: drawnHeight };

  const memberPhotoUrl =
    buildPhotoUrl(
      member.idPhotoPath ||
      member.profilePicture ||
      member.photoUrl ||
      member.photoURL ||
      member.profilePhoto ||
      member.photo ||
      member.avatarUrl ||
      member.profileImage ||
      '',
    );

  let photoData = null;
  const memberPhoto = await tryLoadTemplateImage(memberPhotoUrl);
  if (memberPhoto) {
    photoData = imageToDataUrl(memberPhoto, {
      format: 'image/jpeg',
      quality: PHOTO_IMAGE_QUALITY,
      scale: 1.8,
    });
  }
  if (!photoData && memberPhotoUrl) {
    photoData = await fetchImageDataUrl(memberPhotoUrl);
  }

  if (photoData) {
    const targetWidth = dimensions.width * (PHOTO_FRAME.width / TEMPLATE_COORD_BASE.width);
    const targetHeight = dimensions.height * (PHOTO_FRAME.height / TEMPLATE_COORD_BASE.height);
    const targetX = origin.x + (PHOTO_FRAME.x / TEMPLATE_COORD_BASE.width) * dimensions.width;
    const targetY = origin.y + (PHOTO_FRAME.y / TEMPLATE_COORD_BASE.height) * dimensions.height;

    const intrinsicWidth = memberPhoto?.naturalWidth;
    const intrinsicHeight = memberPhoto?.naturalHeight;
    const scalePhoto = Math.min(
      targetWidth / (intrinsicWidth || PHOTO_FRAME.width),
      targetHeight / (intrinsicHeight || PHOTO_FRAME.height),
    );

    const renderWidth = (intrinsicWidth || PHOTO_FRAME.width) * scalePhoto;
    const renderHeight = (intrinsicHeight || PHOTO_FRAME.height) * scalePhoto;

    // Slightly nudge photo to the left while keeping vertical centering
    const leftNudge = targetWidth * 0.035; // stronger left shift to better align in frame
    const renderX = targetX - leftNudge;
    const renderY = targetY + (targetHeight - renderHeight) / 2;

    const format = photoData.startsWith('data:image/jpeg') || photoData.startsWith('data:image/jpg') ? 'JPEG' : 'PNG';
    doc.addImage(photoData, format, renderX, renderY, renderWidth, renderHeight, undefined, 'FAST');
  }

  // Signature
  const signatureUrl = buildPhotoUrl(
    member.signaturePath
    || member.signatureFilePath
    || member.signatureUrl
    || member.signature
    || '',
  );

  let signatureData = null;
  const signatureImage = await tryLoadTemplateImage(signatureUrl);
  if (signatureImage) {
    signatureData = imageToDataUrl(signatureImage, {
      format: 'image/png',
      scale: 1.6,
    });
  }
  if (!signatureData && signatureUrl) {
    signatureData = await fetchImageDataUrl(signatureUrl);
  }

  if (signatureData) {
    const targetWidth = dimensions.width * (SIGNATURE_AREA.width / TEMPLATE_COORD_BASE.width);
    const targetHeight = dimensions.height * (SIGNATURE_AREA.height / TEMPLATE_COORD_BASE.height);
    const targetX = origin.x + (SIGNATURE_AREA.x / TEMPLATE_COORD_BASE.width) * dimensions.width;
    const targetY = origin.y + (SIGNATURE_AREA.y / TEMPLATE_COORD_BASE.height) * dimensions.height;

    const intrinsicWidth = signatureImage?.naturalWidth;
    const intrinsicHeight = signatureImage?.naturalHeight;
    const scaleSignature = Math.min(
      targetWidth / (intrinsicWidth || SIGNATURE_AREA.width),
      targetHeight / (intrinsicHeight || SIGNATURE_AREA.height),
    );

    const renderWidth = (intrinsicWidth || SIGNATURE_AREA.width) * scaleSignature;
    const renderHeight = (intrinsicHeight || SIGNATURE_AREA.height) * scaleSignature;
    const renderX = targetX + (targetWidth - renderWidth) / 2;
    const renderY = targetY + (targetHeight - renderHeight) / 2;

    const format = signatureData.startsWith('data:image/jpeg') || signatureData.startsWith('data:image/jpg') ? 'JPEG' : 'PNG';
    doc.addImage(signatureData, format, renderX, renderY, renderWidth, renderHeight, undefined, 'FAST');
  }

  const emergencyContact = parseEmergencyContact(member.emergencyContact ?? member.emergencyContactName);

  drawField(doc, member.fullName, FIELD_COORDS.fullName, origin, dimensions);
  drawField(doc, member.placeOfBirth ?? member.birthPlace, FIELD_COORDS.placeOfBirth, origin, dimensions);
  const dobParts = splitDateParts(member.dateOfBirth);
  const month = (dobParts.mm || '  ').padEnd(2, ' ');
  const day = (dobParts.dd || '  ').padEnd(2, ' ');
  const year = (dobParts.yy || '  ').padEnd(2, ' ');
  drawField(doc, month[0], paddedCoords(FIELD_COORDS.dateOfBirthMonth1), origin, dimensions);
  drawField(doc, month[1], paddedCoords(FIELD_COORDS.dateOfBirthMonth2), origin, dimensions);
  drawField(doc, day[0], paddedCoords(FIELD_COORDS.dateOfBirthDay1), origin, dimensions);
  drawField(doc, day[1], paddedCoords(FIELD_COORDS.dateOfBirthDay2), origin, dimensions);
  drawField(doc, year[0], paddedCoords(FIELD_COORDS.dateOfBirthYear1), origin, dimensions);
  drawField(doc, year[1], paddedCoords(FIELD_COORDS.dateOfBirthYear2), origin, dimensions);
  drawField(doc, member.address, FIELD_COORDS.address, origin, dimensions);
  drawField(doc, calculateAge(member.dateOfBirth), FIELD_COORDS.age, origin, dimensions);
  drawField(doc, member.education, FIELD_COORDS.education, origin, dimensions);
  drawField(doc, member.numberOfChildren ?? member.childrenCount, FIELD_COORDS.children, origin, dimensions);
  drawField(doc, member.religion, FIELD_COORDS.religion, origin, dimensions);
  drawField(doc, member.mobile ?? member.phone, FIELD_COORDS.contactNumber, origin, dimensions);
  drawField(doc, member.email, FIELD_COORDS.email, origin, dimensions);

  drawField(doc, member.company, FIELD_COORDS.employer, origin, dimensions);
  drawField(doc, member.position, FIELD_COORDS.position, origin, dimensions);
  drawField(
    doc,
    member.department
      ? member.department.replace(/\s*\([^)]*\)/g, '').trim()
      : '',
    FIELD_COORDS.unitSection,
    origin,
    dimensions
  );
  drawField(
    doc,
    member.yearsEmployed != null
      ? (() => {
          const years = parseInt(member.yearsEmployed, 10);
          if (Number.isNaN(years) || years <= 0) return '';
          return `${years} ${years === 1 ? 'year' : 'years'}`;
        })()
      : '',
    FIELD_COORDS.yearsEmployed,
    origin,
    dimensions
  );
  drawField(doc, member.unionAffiliation, FIELD_COORDS.unionName, origin, dimensions);
  drawField(doc, member.unionPosition, FIELD_COORDS.unionPosition, origin, dimensions);
  drawField(doc, formatDateText(member.registeredDate ?? member.joinDate), FIELD_COORDS.membershipDate, origin, dimensions);

  drawField(doc, emergencyContact.name, FIELD_COORDS.emergencyName, origin, dimensions);
  drawField(doc, emergencyContact.address, FIELD_COORDS.emergencyAddress, origin, dimensions);
  drawField(doc, emergencyContact.phone, FIELD_COORDS.emergencyContact, origin, dimensions);
  drawField(doc, emergencyContact.relationship, FIELD_COORDS.emergencyRelationship, origin, dimensions);

  const statusValue = normalize(member.maritalStatus).toLowerCase();
  if (statusValue) {
    const key = (() => {
      if (statusValue.startsWith('sing')) return 'single';
      if (statusValue.startsWith('mar')) return 'married';
      if (statusValue.startsWith('div') || statusValue.startsWith('sep') || statusValue.startsWith('wid')) return 'divorce';
      return 'others';
    })();
    markCheckbox(doc, STATUS_CHECKBOXES[key], origin, dimensions);
    if (key === 'others') {
      drawField(doc, member.maritalStatus, FIELD_COORDS.statusOther, origin, dimensions);
    }
  }

  const rawGenderValue = normalize(member.gender);
  const genderValue = rawGenderValue.toLowerCase();
  const customGenderValue = normalize(member.genderOther ?? member.otherGender);
  if (genderValue) {
    const genderKey = (() => {
      if (genderValue.startsWith('m')) return 'man';
      if (genderValue.startsWith('f')) return 'woman';
      if (genderValue.includes('prefer')) return 'prefer';
      return 'other';
    })();
    markCheckbox(doc, GENDER_CHECKBOXES[genderKey], origin, dimensions);
    if (genderKey === 'other') {
      const fallbackCustomGender = ['other', 'others', 'other (please state)'].includes(genderValue)
        ? ''
        : rawGenderValue;
      const genderOtherText = customGenderValue || fallbackCustomGender;
      drawField(doc, genderOtherText, FIELD_COORDS.genderOther, origin, dimensions);
    }
  }

  return doc;
};

export const getMembershipFormPreviewUrl = async (member = {}, options = {}) => {
  const doc = await buildMembershipFormPdf(member, options);
  return doc.output('bloburl');
};

export const exportMembershipForm = async (member = {}, options = {}) => {
  const doc = await buildMembershipFormPdf(member, options);
  const unionPrefix = (await getUnionConfig(member.unionAffiliation)).short.toLowerCase();
  const filenameSafeId = normalize(member.memberID ?? member.fullName ?? `${unionPrefix}-member`)
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase();
  const today = new Date().toISOString().split('T')[0];
  doc.save(`${filenameSafeId || `${unionPrefix}-member`}-membership-${today}.pdf`);
};
